import type { TableHeaderProps } from '@ksyos/design-system/dist/components/table';
import type { SingleValue } from 'react-select';
import {
    Button,
    ButtonRow,
    Card,
    Heading,
    IconButton,
    Select,
    Table,
    TableBody,
    TableCell,
    TableCellExpanded,
    TableCellExpandRow,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    TableRowExpanded,
} from '@ksyos/design-system';
import { orderBy } from 'lodash';
import { Fragment, useMemo, useState } from 'react';
import type {
    AdminProcessActivity,
    AdminProcessProcessWorkflowStep,
    AdminProcessTask,
    IAdminProcess,
    IServiceActivity,
} from '@common/interfaces';
import type { UpdateProcessWorkflowStepsWithActivitiesAndTasksRequestBody } from '@common/interfaces/api/processesApi';
import type {
    IServiceWorkflowStep,
    IServiceWorkflowStepActivity,
    IServiceWorkflowTransition,
} from '@frontend/components/pages/services/workflowGraph/WorkflowGraph';
import {
    isProcessActivityStatus,
    isProcessTaskStatus,
    isStepStatus,
    ManagerAuthorization,
    ProcessActivityStatus,
    ProcessTaskStatus,
    StepStatus,
    ToastType,
} from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import { getDisplayName } from '@common/utils/getDisplayName';
import {
    mapProcessActivityStatusToStepStatus,
    mapProcessTaskStatusToStepStatus,
    mapProcessWorkflowStepStatusToActivityStatus,
    mapProcessWorkflowStepStatusToTaskStatus,
} from '@common/utils/mapWorkflowStatusses';
import { nonNullable } from '@common/utils/nonNullableTypeUtils';
import { Modal } from '@frontend/components/elements/modals/Modal';
import { NullableFormattedDatetime } from '@frontend/components/elements/NullableFormattedDatetime';
import { updateProcessWorkflowStepsWithActivitiesAndTasks } from '@frontend/services/api/processesApiService';
import { hasOneAuthorization } from '@frontend/utils';
import { showToast } from '@frontend/utils/showToast';
import { WorkflowGraph } from '../../services/workflowGraph/WorkflowGraph';

type NewAdminProcessDetailsStepsProps = Readonly<{
    onReloadProcess: () => void;
    process: IAdminProcess;
    serviceActivities: IServiceActivity[];
    serviceWorkflowStepActivities: IServiceWorkflowStepActivity[];
    serviceWorkflowSteps: IServiceWorkflowStep[];
    serviceWorkflowTransitions: IServiceWorkflowTransition[];
}>;

type CompleteProcessWorkflowStep = AdminProcessProcessWorkflowStep & {
    activity?: AdminProcessActivity;
    serviceWorkflowStep: IServiceWorkflowStep;
    tasks?: AdminProcessTask[];
};

type Status = ProcessActivityStatus | ProcessTaskStatus | StepStatus;

type Type = AdminProcessActivity | AdminProcessProcessWorkflowStep | AdminProcessTask;

/*
 * TODO: CS-25339 Improve design of this component
 */
export function WorkflowSteps({
    onReloadProcess,
    process,
    serviceActivities,
    serviceWorkflowStepActivities,
    serviceWorkflowSteps,
    serviceWorkflowTransitions,
}: NewAdminProcessDetailsStepsProps) {
    const canUpdate = hasOneAuthorization(ManagerAuthorization.updateProcessWorkflowStepsAndActivities);
    const [expandedRows, setExpandedRows] = useState<string[]>([]);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [modalGraphMode, setModalGraphMode] = useState<'current' | 'edited'>('current');
    const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('desc');
    const [sortKey, setSortKey] = useState<string>('updated_at');
    const [editedProcess, setEditedProcess] = useState<{
        [id: string]: {
            action?: string;
            status?: Status;
            type: string;
        };
    }>({});
    const [workflowGraphProcess, setWorkflowGraphProcess] = useState<IAdminProcess>();

    const completeProcessWorkflowSteps: CompleteProcessWorkflowStep[] = useMemo(() => {
        if (process.processWorkflowSteps.length == 0 || serviceWorkflowSteps.length == 0) {
            return [];
        }

        return process.processWorkflowSteps.map((processWorkflowStep) => {
            const serviceWorkflowStep = serviceWorkflowSteps.find(
                (sws) => sws.id === processWorkflowStep.serviceWorkflowStepId
            );
            const activity = process.activities.find(
                (processActivity) => processActivity.processWorkflowStepId === processWorkflowStep.id
            );
            const tasks = process.tasks.filter((processTask) =>
                isRelatedProcessWorkflowStep(processTask, processWorkflowStep)
            );

            return {
                ...processWorkflowStep,
                activity,
                serviceWorkflowStep: nonNullable(serviceWorkflowStep), // deze wordt pas gezet als de serviceWorkflowSteps zijn geladen, vandaar dat hij optioneel is
                tasks,
            };
        });
    }, [process.processWorkflowSteps, serviceWorkflowSteps]);
    const orderedProcessWorkflowSteps = useMemo(
        () => orderBy(completeProcessWorkflowSteps, sortKey, orderDirection),
        [completeProcessWorkflowSteps, orderDirection, sortKey]
    );

    return (
        <>
            <Heading
                mb={6}
                size="large"
            >
                {getLanguage().processDetails.steps}
            </Heading>
            <Card>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader />
                                <TableHeader {...getTableHeaderProps('serviceWorkflowStep.name')}>
                                    {getLanguage().common.name}:
                                </TableHeader>
                                <TableHeader {...getTableHeaderProps('created_at')}>
                                    {getLanguage().processDetails.createdAt}:
                                </TableHeader>
                                <TableHeader {...getTableHeaderProps('updated_at')}>
                                    {getLanguage().processDetails.updatedAt}:
                                </TableHeader>
                                <TableHeader {...getTableHeaderProps('performerPractitioner.nameGiven')}>
                                    {getLanguage().processDetails.performedByPractitioner}:
                                </TableHeader>
                                <TableHeader {...getTableHeaderProps('status')}>
                                    {getLanguage().common.status}
                                </TableHeader>
                                <TableHeader></TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orderedProcessWorkflowSteps.map((processWorkflowStep) => {
                                const isExpanded = expandedRows.includes(processWorkflowStep.id);

                                return (
                                    <Fragment key={processWorkflowStep.id}>
                                        <TableRow>
                                            <TableCellExpandRow
                                                isExpandable={
                                                    Boolean(processWorkflowStep.activity) ||
                                                    Boolean(processWorkflowStep.tasks?.length)
                                                }
                                                isExpanded={isExpanded}
                                                onExpand={() => handleOnExpand(processWorkflowStep.id)}
                                            />
                                            <TableCell>{processWorkflowStep.serviceWorkflowStep.name}</TableCell>
                                            <TableCell>
                                                <NullableFormattedDatetime value={processWorkflowStep.created_at} />
                                            </TableCell>
                                            <TableCell>
                                                <NullableFormattedDatetime value={processWorkflowStep.updated_at} />
                                            </TableCell>
                                            <TableCell>
                                                {processWorkflowStep.performerPractitioner
                                                    ? getDisplayName(processWorkflowStep.performerPractitioner)
                                                    : null}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    backgroundColor:
                                                        editedProcess[processWorkflowStep.id]?.action === 'setStatus'
                                                            ? 'yellow !important'
                                                            : 'auto !important',
                                                }}
                                            >
                                                {canUpdate ? (
                                                    <StepStatusSelect
                                                        onChange={(value) =>
                                                            updateWorkflowStepStatus(
                                                                processWorkflowStep,
                                                                isStepStatus(value?.value) ? value.value : undefined
                                                            )
                                                        }
                                                        value={
                                                            editedProcess[processWorkflowStep.id]?.action ===
                                                            'setStatus'
                                                                ? editedProcess[processWorkflowStep.id]?.status
                                                                : processWorkflowStep.status
                                                        }
                                                    />
                                                ) : (
                                                    processWorkflowStep.status
                                                )}
                                            </TableCell>
                                            {canUpdate && (
                                                <TableCell
                                                    sx={{
                                                        backgroundColor:
                                                            editedProcess[processWorkflowStep.id]?.action === 'delete'
                                                                ? 'red !important'
                                                                : 'auto !important',
                                                    }}
                                                >
                                                    <IconButton
                                                        icon="delete"
                                                        labelText="edit"
                                                        onClick={() => toggleDeleteElem('step', processWorkflowStep.id)}
                                                    />
                                                </TableCell>
                                            )}
                                        </TableRow>

                                        {processWorkflowStep.activity && (
                                            <TableRowExpanded isExpanded={isExpanded}>
                                                <TableCellExpanded>{getLanguage().common.activity}:</TableCellExpanded>
                                                <TableCellExpanded>
                                                    {processWorkflowStep.activity.serviceActivity.name}
                                                </TableCellExpanded>
                                                <TableCell>
                                                    <NullableFormattedDatetime
                                                        value={processWorkflowStep.activity.created_at}
                                                    />
                                                </TableCell>
                                                <TableCellExpanded>
                                                    <NullableFormattedDatetime
                                                        value={processWorkflowStep.activity.updated_at}
                                                    />
                                                </TableCellExpanded>
                                                <TableCellExpanded>
                                                    {processWorkflowStep.activity.performerPractitioner
                                                        ? getDisplayName(
                                                              processWorkflowStep.activity.performerPractitioner
                                                          )
                                                        : null}
                                                </TableCellExpanded>
                                                <TableCellExpanded
                                                    sx={{
                                                        backgroundColor:
                                                            editedProcess[processWorkflowStep.activity.id]?.action ==
                                                            'setStatus'
                                                                ? 'yellow !important'
                                                                : 'auto !important',
                                                    }}
                                                >
                                                    {canUpdate ? (
                                                        <ProcessActivityStatusSelect
                                                            onChange={(value) => {
                                                                updateActivityStatus(
                                                                    nonNullable(processWorkflowStep.activity),
                                                                    isProcessActivityStatus(value?.value)
                                                                        ? value.value
                                                                        : undefined
                                                                );
                                                            }}
                                                            value={
                                                                editedProcess[processWorkflowStep.activity.id]
                                                                    ?.action === 'setStatus'
                                                                    ? editedProcess[processWorkflowStep.activity.id]
                                                                          ?.status
                                                                    : processWorkflowStep.activity.status
                                                            }
                                                        />
                                                    ) : (
                                                        processWorkflowStep.activity.status
                                                    )}
                                                </TableCellExpanded>
                                                {canUpdate && (
                                                    <TableCellExpanded
                                                        sx={{
                                                            backgroundColor:
                                                                editedProcess[processWorkflowStep.activity.id]
                                                                    ?.action === 'delete'
                                                                    ? 'red !important'
                                                                    : 'auto !important',
                                                        }}
                                                    >
                                                        <IconButton
                                                            icon="delete"
                                                            labelText="edit"
                                                            onClick={() =>
                                                                toggleDeleteElem(
                                                                    'activity',
                                                                    processWorkflowStep.activity?.id ?? ''
                                                                )
                                                            }
                                                        />
                                                    </TableCellExpanded>
                                                )}
                                            </TableRowExpanded>
                                        )}

                                        {processWorkflowStep.tasks?.map((task) => (
                                            <TableRowExpanded
                                                isExpanded={isExpanded}
                                                key={task.id}
                                            >
                                                <TableCellExpanded>{getLanguage().common.task}:</TableCellExpanded>
                                                <TableCellExpanded>{task.serviceWorkflowStep.name}</TableCellExpanded>
                                                <TableCellExpanded>
                                                    <NullableFormattedDatetime value={task.created_at} />
                                                </TableCellExpanded>
                                                <TableCellExpanded>
                                                    <NullableFormattedDatetime value={task.updated_at} />
                                                </TableCellExpanded>
                                                <TableCellExpanded>
                                                    {task.performerPractitioner
                                                        ? getDisplayName(task.performerPractitioner)
                                                        : null}
                                                </TableCellExpanded>
                                                <TableCellExpanded
                                                    sx={{
                                                        backgroundColor:
                                                            editedProcess[task.id]?.action === 'setStatus'
                                                                ? 'yellow !important'
                                                                : 'auto !important',
                                                    }}
                                                >
                                                    {canUpdate ? (
                                                        <ProcessTaskStatusSelect
                                                            onChange={(value) =>
                                                                updateTaskStatus(
                                                                    task,
                                                                    isProcessTaskStatus(value?.value)
                                                                        ? value.value
                                                                        : undefined
                                                                )
                                                            }
                                                            value={
                                                                editedProcess[task.id]?.action === 'setStatus'
                                                                    ? editedProcess[task.id]?.status
                                                                    : task.status
                                                            }
                                                        />
                                                    ) : (
                                                        task.status
                                                    )}
                                                </TableCellExpanded>
                                                {canUpdate && (
                                                    <TableCellExpanded
                                                        sx={{
                                                            backgroundColor:
                                                                editedProcess[task.id]?.action === 'delete'
                                                                    ? 'red !important'
                                                                    : 'auto !important',
                                                        }}
                                                    >
                                                        <IconButton
                                                            icon="delete"
                                                            labelText="edit"
                                                            onClick={() => toggleDeleteElem('task', task.id)}
                                                        />
                                                    </TableCellExpanded>
                                                )}
                                            </TableRowExpanded>
                                        ))}
                                    </Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                {canUpdate && (
                    <Card.Footer>
                        <ButtonRow justify="right">
                            <Button
                                color="secondary"
                                isDisabled={Object.keys(editedProcess).length === 0}
                                onClick={() => editProcessCancelEdits()}
                                variant="outline"
                            >
                                {getLanguage().processDetails.cancelEdits}
                            </Button>
                            <Button
                                color="secondary"
                                isDisabled={Object.keys(editedProcess).length === 0}
                                onClick={() => setModalIsOpen(true)}
                                variant="solid"
                            >
                                {getLanguage().processDetails.verifyUpdateSteps}
                            </Button>
                        </ButtonRow>
                    </Card.Footer>
                )}
            </Card>

            {modalIsOpen && (
                <Modal
                    actionButtons={[
                        <Button
                            color="secondary"
                            key="current"
                            onClick={() => setModalGraphMode('current')}
                            variant={modalGraphMode === 'current' ? 'solid' : 'outline'}
                        >
                            {getLanguage().processDetails.showCurrent}
                        </Button>,
                        <Button
                            color="secondary"
                            key="edited"
                            onClick={() => setModalGraphMode('edited')}
                            variant={modalGraphMode === 'edited' ? 'solid' : 'outline'}
                        >
                            {getLanguage().processDetails.showEdited}
                        </Button>,
                        <Button
                            key="cancelEdit"
                            onClick={() => {
                                setModalIsOpen(false);
                                editProcessCancelEdits();
                            }}
                            variant="outline"
                        >
                            {getLanguage().processDetails.cancelEdits}
                        </Button>,
                        <Button
                            key="doUpdate"
                            onClick={() => doUpdateSteps()}
                            variant="solid"
                        >
                            {getLanguage().processDetails.doUpdateSteps}
                        </Button>,
                    ]}
                    isOpen={modalIsOpen}
                    onClose={() => setModalIsOpen(false)}
                    size="fullscreen"
                    title={getLanguage().processDetails.doUpdateSteps}
                >
                    <WorkflowGraph
                        process={workflowGraphProcess && modalGraphMode === 'edited' ? workflowGraphProcess : process}
                        serviceActivities={serviceActivities}
                        serviceWorkflowStepActivities={serviceWorkflowStepActivities}
                        serviceWorkflowSteps={serviceWorkflowSteps}
                        serviceWorkflowTransitions={serviceWorkflowTransitions}
                    />
                </Modal>
            )}
        </>
    );

    function getTableHeaderProps(key: string): TableHeaderProps {
        return {
            isSortHeader: sortKey === key,
            isSortable: true,
            onClick: () => {
                if (sortKey === key) {
                    setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
                } else {
                    setSortKey(key);
                    setOrderDirection('asc');
                }
            },
            // TODO: CS-25338 Make Design System casing work better with Lodash
            sortDirection: orderDirection === 'asc' ? 'ASC' : 'DESC',
        };
    }

    function handleOnExpand(id: string) {
        setExpandedRows((prevExpandedRows) =>
            prevExpandedRows.includes(id) ? prevExpandedRows.filter((item) => item !== id) : [...prevExpandedRows, id]
        );
    }

    function editProcessCancelEdits() {
        setEditedProcess({});
        setWorkflowGraphProcess(undefined);
    }

    function toggleDeleteElem(type: string, elemId: string) {
        if (editedProcess[elemId]?.action === 'delete') {
            unsetDeleteElem(type, elemId);
        } else {
            setDeleteElem(type, elemId);
        }
    }

    function setDeleteElem(type: string, elemId: string) {
        editedProcess[elemId] ||= { type };
        editedProcess[elemId].action = 'delete';
        delete editedProcess[elemId].status;
        if (type === 'step') {
            // delete underlying activities and tasks
            for (const activity of process.activities) {
                if (activity.processWorkflowStepId === elemId) {
                    editedProcess[activity.id] ||= { type: 'activity' };
                    editedProcess[activity.id].action = 'delete';
                    delete editedProcess[activity.id].status;
                }
            }
            const processWorkflowStep = process.processWorkflowSteps.find((step) => step.id === elemId);
            if (processWorkflowStep) {
                for (const task of process.tasks) {
                    if (isRelatedProcessWorkflowStep(task, processWorkflowStep)) {
                        editedProcess[task.id] ||= { type: 'task' };
                        editedProcess[task.id].action = 'delete';
                        delete editedProcess[task.id].status;
                    }
                }
            }
        }
        setEditedProcess({ ...editedProcess });
        updateWorkflowGraphProcess();
    }

    function unsetDeleteElem(type: string, elemId: string) {
        editedProcess[elemId] ||= { type };
        editedProcess[elemId].action = '';
        delete editedProcess[elemId].status;
        if (type !== 'step') {
            // handle related step
            let workflowStepId;
            const activity = process.activities.find((a) => a.id === elemId);
            if (activity) {
                workflowStepId = activity.processWorkflowStepId;
            }
            const task = process.tasks.find((t) => t.id === elemId);
            if (task) {
                const workflowStep = process.processWorkflowSteps.find((step) =>
                    isRelatedProcessWorkflowStep(task, step)
                );
                workflowStepId = workflowStep?.id;
            }
            if (workflowStepId) {
                if (editedProcess[workflowStepId]?.action === 'delete') {
                    editedProcess[workflowStepId].action = '';
                    delete editedProcess[workflowStepId].status;
                } else {
                    if (activity) {
                        const mappedStatus = mapProcessActivityStatusToStepStatus(activity.status);
                        const workflowStep = process.processWorkflowSteps.find((step) => step.id == workflowStepId);
                        if (
                            workflowStep &&
                            mappedStatus !==
                                (editedProcess[workflowStepId]
                                    ? editedProcess[workflowStepId].status
                                    : workflowStep.status)
                        ) {
                            updateWorkflowStepStatus(workflowStep, mappedStatus, 'activity');
                        }
                    }
                    if (task) {
                        const mappedStatus = mapProcessTaskStatusToStepStatus(task.status);
                        const workflowStep = process.processWorkflowSteps.find((step) => step.id == workflowStepId);
                        if (
                            workflowStep &&
                            mappedStatus !==
                                (editedProcess[workflowStepId]
                                    ? editedProcess[workflowStepId].status
                                    : workflowStep.status)
                        ) {
                            updateWorkflowStepStatus(workflowStep, mappedStatus, 'task');
                        }
                    }
                }
            }
        }
        setEditedProcess({ ...editedProcess });
        updateWorkflowGraphProcess();
    }

    function updateElemStatus(type: string, elemId: string, status?: Status) {
        editedProcess[elemId] ||= { type };

        let elementType: Type[];
        switch (type) {
            case 'activity':
                elementType = process.activities;
                break;
            case 'step':
                elementType = process.processWorkflowSteps;
                break;
            case 'task':
                elementType = process.tasks;
                break;
            default:
                throw Error('Unknown type');
        }

        if (elementType.find((element) => element.id === elemId)?.status === status) {
            editedProcess[elemId].action = '';
            editedProcess[elemId].status = undefined;
        } else {
            editedProcess[elemId].action = 'setStatus';
            editedProcess[elemId].status = status;
        }
    }

    function updateActivityStatus(
        processActivity: AdminProcessActivity,
        status?: ProcessActivityStatus,
        caller?: string
    ) {
        updateElemStatus('activity', processActivity.id, status);
        setEditedProcess({ ...editedProcess });
        updateWorkflowGraphProcess();
        if (caller || !status) {
            return;
        }
        const mappedStatus = mapProcessActivityStatusToStepStatus(status);
        const target = process.activities.find((activity) => activity.id === processActivity.id);
        const processWorkflowStep = process.processWorkflowSteps.find(
            (pws) => pws.id === target?.processWorkflowStepId
        );
        if (
            processWorkflowStep &&
            mappedStatus !==
                (editedProcess[processWorkflowStep.id]
                    ? editedProcess[processWorkflowStep.id].status
                    : processWorkflowStep.status)
        ) {
            updateWorkflowStepStatus(processWorkflowStep, mappedStatus, 'activity');
        }
    }

    function updateTaskStatus(processTask: AdminProcessTask, status?: ProcessTaskStatus, caller?: string) {
        updateElemStatus('task', processTask.id, status);
        setEditedProcess({ ...editedProcess });
        updateWorkflowGraphProcess();
        if (caller || !status) {
            return;
        }
        const mappedStatus = mapProcessTaskStatusToStepStatus(status);
        const processWorkflowStep = process.processWorkflowSteps.find((pws) =>
            isRelatedProcessWorkflowStep(processTask, pws)
        );
        if (
            processWorkflowStep &&
            mappedStatus !==
                (editedProcess[processWorkflowStep.id]
                    ? editedProcess[processWorkflowStep.id].status
                    : processWorkflowStep.status)
        ) {
            updateWorkflowStepStatus(processWorkflowStep, mappedStatus, 'task');
        }
    }

    function updateWorkflowStepStatus(
        processWorkflowStep: AdminProcessProcessWorkflowStep,
        status?: Status,
        caller?: string
    ) {
        updateElemStatus('step', processWorkflowStep.id, status);
        setEditedProcess({ ...editedProcess });
        updateWorkflowGraphProcess();
        if (caller !== 'activity' && isStepStatus(status)) {
            const mappedActivityStatus = mapProcessWorkflowStepStatusToActivityStatus(status);
            const activities = process.activities.filter((a) => a.processWorkflowStepId == processWorkflowStep.id);
            for (const activity of activities) {
                // special: if new status is available delete activity
                if (status === StepStatus.available) {
                    setDeleteElem('activity', activity.id);
                } else {
                    updateActivityStatus(activity, mappedActivityStatus, 'step');
                }
            }
        }
        if (caller !== 'task' && isStepStatus(status)) {
            const mappedTaskStatus = mapProcessWorkflowStepStatusToTaskStatus(status);
            const tasks = process.tasks.filter((processTask) =>
                isRelatedProcessWorkflowStep(processTask, processWorkflowStep)
            );

            for (const task of tasks) {
                updateTaskStatus(task, mappedTaskStatus, 'step');
            }
        }
    }

    function updateWorkflowGraphProcess() {
        // copy process
        const result = JSON.parse(JSON.stringify(process));
        // apply edits
        for (const elemId of Object.keys(editedProcess)) {
            let target;
            switch (editedProcess[elemId].type) {
                case 'activity':
                    target = 'activities';
                    break;
                case 'task':
                    target = 'tasks';
                    break;
                case 'step':
                    target = 'processWorkflowSteps';
                    break;
                default:
                    throw Error('Unknown type');
            }
            if (editedProcess[elemId].type === 'activity') {
                target = 'activities';
            }
            if (editedProcess[elemId].type === 'task') {
                target = 'tasks';
            }
            const element = result[target].find((elem: IServiceWorkflowStep) => elem.id === elemId);
            if (editedProcess[elemId].action === 'delete') {
                result[target] = result[target].filter((elem: IServiceWorkflowStep) => elem.id !== elemId);
            }
            if (editedProcess[elemId].action === 'setStatus') {
                element.status = editedProcess[elemId].status;
            }
        }
        setWorkflowGraphProcess(result);
        setModalGraphMode('edited');
    }

    async function doUpdateSteps() {
        const data: UpdateProcessWorkflowStepsWithActivitiesAndTasksRequestBody = {
            deleteProcessActivities: [],
            deleteProcessTasks: [],
            deleteProcessWorkflowSteps: [],
            updateProcessActivities: [],
            updateProcessTasks: [],
            updateProcessWorkflowSteps: [],
        };

        for (const elemId of Object.keys(editedProcess)) {
            if (editedProcess[elemId].action === 'delete') {
                if (editedProcess[elemId].type === 'activity') {
                    data.deleteProcessActivities?.push({ id: elemId });
                }
                if (editedProcess[elemId].type === 'task') {
                    data.deleteProcessTasks?.push({ id: elemId });
                }
                if (editedProcess[elemId].type === 'step') {
                    data.deleteProcessWorkflowSteps?.push({ id: elemId });
                }
            }
            if (editedProcess[elemId].action === 'setStatus') {
                if (
                    editedProcess[elemId].type === 'activity' &&
                    isProcessActivityStatus(editedProcess[elemId].status)
                ) {
                    data.updateProcessActivities?.push({
                        id: elemId,
                        status: editedProcess[elemId].status,
                    });
                }
                if (editedProcess[elemId].type === 'task' && isProcessTaskStatus(editedProcess[elemId].status)) {
                    data.updateProcessTasks?.push({ id: elemId, status: editedProcess[elemId].status });
                }
                if (editedProcess[elemId].type === 'step' && isStepStatus(editedProcess[elemId].status)) {
                    data.updateProcessWorkflowSteps?.push({ id: elemId, status: editedProcess[elemId].status });
                }
            }
        }

        try {
            await updateProcessWorkflowStepsWithActivitiesAndTasks(process.id, data);
            showToast(ToastType.success, getLanguage().processDetails.updatesDone);
            setExpandedRows([]);
            setModalGraphMode('current');
            setModalIsOpen(false);
            setEditedProcess({});
        } catch {
            showToast(ToastType.error, getLanguage().processDetails.updatesFailed);
        }

        onReloadProcess();
    }
}

type SelectValue = SingleValue<{
    label?: string;
    value?: string;
}>;

type SelectProps = Readonly<{
    onChange: (value: SelectValue) => void;
    value?: string;
}>;

function ProcessActivityStatusSelect({ onChange, value }: SelectProps) {
    return (
        <Select
            onChange={onChange}
            options={Object.values(ProcessActivityStatus).map((status) => ({
                label: status,
                value: status,
            }))}
            size="medium"
            value={{ label: value, value }}
        />
    );
}

function ProcessTaskStatusSelect({ onChange, value }: SelectProps) {
    return (
        <Select
            onChange={onChange}
            options={Object.values(ProcessTaskStatus).map((status) => ({
                label: status,
                value: status,
            }))}
            size="medium"
            value={{ label: value, value }}
        />
    );
}

function StepStatusSelect({ onChange, value }: SelectProps) {
    return (
        <Select
            onChange={onChange}
            options={Object.values(StepStatus).map((status) => ({
                label: status,
                value: status,
            }))}
            size="medium"
            value={{ label: value, value }}
        />
    );
}

function isRelatedProcessWorkflowStep(
    processTask: AdminProcessTask,
    processWorkflowStep: AdminProcessProcessWorkflowStep
) {
    return (
        processTask.processWorkflowStepId === processWorkflowStep.id ||
        (processTask.processWorkflowStepId === null &&
            mapProcessTaskStatusToStepStatus(processTask.status) === processWorkflowStep.status &&
            processTask.serviceWorkflowStepId === processWorkflowStep.serviceWorkflowStepId)
    );
}
